import React, {useMemo} from 'react';
import {useTranslation} from "react-i18next";
import LogoWithLanguageSwitcher from "../../../components/common/LogoWithLanguageSwitcher";
import {brand} from "../../../theme/brand";
import {AuthContainer} from "../Auth.styled";
import {LicenseActivationCard, LicenseErrorContainer, LicensePendingContainer} from "./LicenseActivation.styled";
import {Button, Stack, Typography} from "@mui/material";
import {useAuth} from "../../../contexts/AuthContext";
import LicenseAdminActivationContent
    from "../../admin/LicenseManagement/components/LicenseAdminActivationContent/LicenseAdminActivationContent";

interface LicenseActivationProps {
    pending?: boolean;
    error?: boolean;
    onRetry?: () => void;
}

const LicenseActivation = ({pending = false, error = false, onRetry}: LicenseActivationProps) => {
    const {t} = useTranslation();

    const licenseActivationVariant = useMemo(() => {
        if (pending) {
            return <LicensePendingContent />
        }
        else if (error && onRetry) {
            return <LicenseErrorContent onRetry={onRetry} />
        }

        return <LicenseAdminActivationContent />
    }, [pending, error, onRetry]);

    return (
        <AuthContainer role="region" aria-labelledby="license-required-title">
            <LogoWithLanguageSwitcher src={brand.logo} alt={t('auth.logo.alt')} clickable={true} />

            <LicenseActivationCard>
                {licenseActivationVariant}
            </LicenseActivationCard>
        </AuthContainer>
    );
};

export default LicenseActivation;

const LicensePendingContent = () => {
    const {t} = useTranslation();
    const {logout} = useAuth();

    return (
        <LicensePendingContainer>
            <Typography id="license-required-title" variant="h4" color="text.primary" sx={{ mb: 2 }}>
                {t('licenseActivation.pending.title')}
            </Typography>

            <Typography variant="subtitle1" color="text.primary" mb={4}>
                {t('licenseActivation.pending.description')}
            </Typography>

            <Button type="button" variant="text" onClick={() => logout()}>
                {t('licenseActivation.pending.logoutBtn')}
            </Button>
        </LicensePendingContainer>
    );
};

const LicenseErrorContent = ({onRetry}: {onRetry: () => void;}) => {
    const {t} = useTranslation();
    const {logout} = useAuth();

    return (
        <LicenseErrorContainer>
            <Typography id="license-required-title" variant="h4" color="text.primary" sx={{ mb: 1 }}>
                {t('licenseActivation.error.title')}
            </Typography>

            <Typography variant="subtitle1" color="text.primary">
                {t('licenseActivation.error.description')}
            </Typography>

            <Stack direction="row" justifyContent="space-between" width="100%" sx={{ mt: 3}}>
                <Button type="button" variant="text" onClick={() => logout()}>
                    {t('licenseActivation.error.logoutBtn')}
                </Button>

                <Button type="button" variant="contained" onClick={() => onRetry()}>
                    {t('licenseActivation.error.tryAgainBtn')}
                </Button>
            </Stack>
        </LicenseErrorContainer>
    );
};